<template>
  <div class="documents-management-page" data-testid="documents-view">
    <v-row no-gutters class="mb-8 px-6 px-md-12 px-lg-16 justify-center">
      <v-col cols="12" xl="11" class="pt-6 px-md-4 px-lg-8">

        <!-- Page Header -->
        <div class="page-header mb-6 d-flex align-center justify-space-between flex-wrap">
          <div class="pr-4 mb-2 mb-sm-0">
            <div class="text-h4 font-weight-light page-title">
              {{ $t('documents.title') }}
            </div>
          </div>

          <!-- Direct Refresh for PRO clients -->
          <v-btn
            v-if="isPro"
            outlined
            small
            color="primary"
            :loading="proLoading"
            class="text-capitalize"
            data-testid="documents-refresh"
            @click="fetchProCompanyDocuments"
          >
            <v-icon left small>mdi-refresh</v-icon>
            <span class="white--text">{{ $t('documents.refresh') }}</span>
          </v-btn>
        </div>

        <!-- 1. Client / PRO User View: Direct Company Document Workspace -->
        <div v-if="isPro" class="pro-document-view">
          <v-alert
            v-if="!hasCompany"
            type="info"
            outlined
            prominent
            border="left"
            class="mb-6"
            data-testid="documents-no-company"
          >
            <div class="text-subtitle-1 font-weight-medium">
              {{ $t('documents.noCompanyAssociated') }}
            </div>
          </v-alert>

          <documents-company-detail
            v-else
            :payload="proPayload"
            :loading="proLoading"
            :is-drawer="false"
            :is-pro="isPro"
            :downloading-sha="downloadingSha"
            :downloading-operation="downloadingOperation"
            :zipping-kyc="zippingKyc"
            :zipping-operations="zippingOperations"
            @download-kyc="downloadKycDocument"
            @download-operation="downloadOperationDocument"
            @download-kyc-zip="downloadCompanyZip('kyc')"
            @download-operations-zip="downloadCompanyZip('company-operations')"
          />
        </div>

        <!-- 2. Master Broker & Partner View: Directory & Multi-Tenant Management -->
        <div v-else class="broker-directory-view">
          <!-- Search & Filter Toolbar -->
          <documents-toolbar
            :search.sync="search"
            :filter-mode.sync="filterMode"
            :counts="metrics"
            :loading="loading"
            :is-broker="(isBroker || isRootBroker) && hasPartnerClients"
            @refresh="fetchCompanies"
            @expand-all="onExpandAll"
            @collapse-all="onCollapseAll"
          />

          <!-- Master Broker / Root Broker with split tables -->
          <template v-if="isBroker || isRootBroker">
            <!-- 1. Broker's Direct Clients Table (if any direct clients exist in the book) -->
            <div v-if="hasDirectClients" class="mb-8">
              <div class="documents-section-header d-flex align-center mb-3" data-testid="documents-section-direct">
                <v-icon small color="primary" class="mr-2">mdi-account-multiple</v-icon>
                <span class="text-subtitle-1 font-weight-bold">{{ $t('documents.directClients') }}</span>
                <v-chip x-small color="primaryTransp" text-color="primary" class="ml-3 font-weight-medium">
                  {{ directCompanies.length }} {{ $t(directCompanies.length === 1 ? 'documents.company' : 'documents.companies').toLowerCase() }}
                </v-chip>
              </div>
              <documents-company-table
                ref="directTable"
                :companies="directCompanies"
                :loading="loading"
                :group-by-broker="false"
                :is-broker="false"
                @select="openCompany"
              />
            </div>

            <!-- 2. Broker's Partners & Clients Table (if any partner clients exist in the book) -->
            <div v-if="hasPartnerClients" class="mb-8">
              <div v-if="hasDirectClients" class="documents-section-header d-flex align-center mb-3" data-testid="documents-section-partners">
                <v-icon small color="primary" class="mr-2">mdi-domain</v-icon>
                <span class="text-subtitle-1 font-weight-bold">{{ $t('documents.partnersAndClients') }}</span>
                <v-chip x-small color="primaryTransp" text-color="primary" class="ml-3 font-weight-medium">
                  {{ partnerBrokersCount }} {{ $t(partnerBrokersCount === 1 ? 'documents.partner' : 'documents.partners').toLowerCase() }} &middot; {{ partnerCompanies.length }} {{ $t(partnerCompanies.length === 1 ? 'documents.company' : 'documents.companies').toLowerCase() }}
                </v-chip>
              </div>
              <documents-company-table
                ref="partnerTable"
                :companies="partnerCompanies"
                :loading="loading"
                :group-by-broker="true"
                :is-broker="true"
                @select="openCompany"
              />
            </div>

            <!-- Fallback if neither direct nor partner clients exist yet -->
            <documents-company-table
              v-if="!hasDirectClients && !hasPartnerClients"
              ref="companyTable"
              :companies="filteredCompanies"
              :loading="loading"
              :group-by-broker="false"
              :is-broker="isBroker"
              @select="openCompany"
            />
          </template>

          <!-- Regular Partner view (single table) -->
          <template v-else>
            <documents-company-table
              ref="companyTable"
              :companies="filteredCompanies"
              :loading="loading"
              :group-by-broker="groupByBroker"
              :is-broker="isBroker"
              @select="openCompany"
            />
          </template>

          <!-- Root broker only: records whose company no longer exists. -->
          <documents-dangling-section
            v-if="dangling.length"
            :records="dangling"
            @select="openDangling"
          />
        </div>

      </v-col>
    </v-row>

    <!-- Slide-over Right Drawer for Broker/Partner Drill-down -->
    <documents-company-panel
      v-if="!isPro"
      v-model="panelOpen"
      :loading="panelLoading"
      :payload="panelPayload"
      :downloading-sha="downloadingSha"
      :downloading-operation="downloadingOperation"
      :zipping-kyc="zippingKyc"
      :zipping-operations="zippingOperations"
      @download-kyc="downloadKycDocument"
      @download-operation="downloadOperationDocument"
      @download-kyc-zip="downloadCompanyZip('kyc')"
      @download-operations-zip="downloadCompanyZip('company-operations')"
    />
  </div>
</template>

<script>
import { mapStores } from 'pinia'

import { useExecutionStore, useImportFiles, useSnackbarStore, useAuthStore } from '@/store/index'
import { COMPANY_REFRESH_INTERVAL_MS } from '@/store/company'
import { resolveUserRole, getCurrentBrokerId } from '@/utils.js'

import DocumentsToolbar from '@/components/documents/DocumentsToolbar.vue'
import DocumentsCompanyTable from '@/components/documents/DocumentsCompanyTable.vue'
import DocumentsCompanyPanel from '@/components/documents/DocumentsCompanyPanel.vue'
import DocumentsCompanyDetail from '@/components/documents/DocumentsCompanyDetail.vue'
import DocumentsDanglingSection from '@/components/documents/DocumentsDanglingSection.vue'
import { companyMatchesSearch, triggerBrowserDownload } from '@/components/documents/documentHelpers.js'

export default {
  name: 'DocumentsManagementView',

  components: {
    DocumentsToolbar,
    DocumentsCompanyTable,
    DocumentsCompanyPanel,
    DocumentsCompanyDetail,
    DocumentsDanglingSection,
  },

  data () {
    return {
      loading:    false,
      search:     '',
      filterMode: 'all', // 'all' | 'with_docs' | 'missing_docs'

      companies:    [],
      dangling:     [],
      isRootBroker: false,

      isBroker:  false,
      isPartner: false,

      // PRO / Client state
      proPayload: null,
      proLoading: false,
      isFetchingDocuments: false,
      documentsInterval: null,
      hasCompany: true,

      // Drawer state for Broker / Partner
      panelOpen:    false,
      panelLoading: false,
      panelPayload: null,

      downloadingSha:       null,
      downloadingOperation: null,
      zippingKyc:           false,
      zippingOperations:    false,
    }
  },

  computed: {
    ...mapStores(useExecutionStore),

    isPro () {
      return !this.isBroker && !this.isPartner
    },

    activePayload () {
      return this.isPro ? this.proPayload : this.panelPayload
    },

    companiesWithGroup () {
      const currentBrokerId = getCurrentBrokerId()
      const directClientsLabel = this.$t('documents.directClients')
      return this.companies.map(company => {
        const isDirect = !!(company.broker_id && company.broker_id === currentBrokerId)
        return {
          ...company,
          is_direct: isDirect,
          broker_group: isDirect ? directClientsLabel : (company.broker_name || '-'),
        }
      })
    },

    searchedCompanies () {
      return this.companiesWithGroup.filter(company => companyMatchesSearch(company, this.search))
    },

    metrics () {
      const searched = this.searchedCompanies
      const total = searched.length
      const withDocs = searched.filter(c => (c.document_count || 0) > 0).length
      const missingDocs = total - withDocs
      return { total, withDocs, missingDocs }
    },

    filteredCompanies () {
      return this.searchedCompanies.filter(company => {
        if (this.filterMode === 'with_docs' && !company.document_count) return false
        if (this.filterMode === 'missing_docs' && company.document_count) return false
        return true
      })
    },

    directCompanies () {
      return this.filteredCompanies
        .filter(c => c.is_direct)
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },

    partnerCompanies () {
      return this.filteredCompanies
        .filter(c => !c.is_direct)
        .slice()
        .sort((a, b) => {
          const gComp = (a.broker_group || '').localeCompare(b.broker_group || '')
          if (gComp !== 0) return gComp
          return (a.name || '').localeCompare(b.name || '')
        })
    },

    hasDirectClients () {
      return this.companiesWithGroup.some(c => c.is_direct)
    },

    hasPartnerClients () {
      return this.companiesWithGroup.some(c => !c.is_direct)
    },

    partnerBrokersCount () {
      const groups = new Set(this.partnerCompanies.map(c => c.broker_group).filter(Boolean))
      return groups.size
    },

    groupByBroker () {
      if (this.isBroker || this.isRootBroker) {
        return this.companiesWithGroup.length > 0
      }
      const groups = new Set(this.companiesWithGroup.map(company => company.broker_group))
      return groups.size > 1
    },
  },

  mounted () {
    this.resolveRole()
    if (this.isPro) {
      this.fetchProCompanyDocuments()
      // A Broker attaching or removing a KYC document is the change a Company
      // user is most often waiting on, so this view does not wait for a reload.
      this.documentsInterval = setInterval(
        () => this.fetchProCompanyDocuments({ showLoading: false }),
        COMPANY_REFRESH_INTERVAL_MS
      )
    } else {
      this.fetchCompanies()
    }
  },

  beforeDestroy () {
    clearInterval(this.documentsInterval)
  },

  methods: {
    resolveRole () {
      const role = useAuthStore().getUserRoleFromLocalStorage()
      const flags = resolveUserRole(role)
      this.isBroker  = flags.isBroker
      this.isPartner = flags.isPartner
    },

    // The manual refresh button owns the spinner and the error toast; the
    // background poll must do neither.
    async fetchProCompanyDocuments ({ showLoading = true } = {}) {
      if (this.isFetchingDocuments) return
      const companyId = localStorage.getItem('companyId')
      if (!companyId || companyId === 'null' || companyId === 'undefined') {
        this.hasCompany = false
        return
      }

      this.hasCompany = true
      this.isFetchingDocuments = true
      if (showLoading) this.proLoading = true
      try {
        const res = await this.executionStore.getCompanyDocuments(companyId)
        this.proPayload = res?.data ?? null
      } catch (error) {
        console.error('[DocumentsManagementView] fetchProCompanyDocuments:', error)
        if (showLoading) useSnackbarStore().open(this.$t('documents.loadFailed'), 'error')
      } finally {
        this.isFetchingDocuments = false
        this.proLoading = false
      }
    },

    async fetchCompanies () {
      const brokerId = getCurrentBrokerId()
      if (!brokerId) return

      this.loading = true
      try {
        const res = await this.executionStore.getDocumentReports({ broker_id: brokerId })
        this.companies    = res?.data?.companies ?? []
        this.dangling     = res?.data?.dangling ?? []
        this.isRootBroker = !!res?.data?.is_root_broker
      } catch (error) {
        console.error('[DocumentsManagementView] fetchCompanies:', error)
        useSnackbarStore().open(this.$t('documents.loadFailed'), 'error')
      } finally {
        this.loading = false
      }
    },

    onExpandAll () {
      if (this.$refs.partnerTable) {
        this.$refs.partnerTable.expandAll()
      } else if (this.$refs.companyTable) {
        this.$refs.companyTable.expandAll()
      }
    },

    onCollapseAll () {
      if (this.$refs.partnerTable) {
        this.$refs.partnerTable.collapseAll()
      } else if (this.$refs.companyTable) {
        this.$refs.companyTable.collapseAll()
      }
    },

    openCompany (company) {
      this.loadCompany(company.company_id)
    },

    openDangling (record) {
      this.loadCompany(record.company_id)
    },

    async loadCompany (companyId) {
      this.panelPayload = null
      this.panelOpen = true
      this.panelLoading = true
      try {
        const res = await this.executionStore.getCompanyDocuments(companyId)
        this.panelPayload = res?.data ?? null
      } catch (error) {
        console.error('[DocumentsManagementView] loadCompany:', error)
        useSnackbarStore().open(this.$t('documents.loadFailed'), 'error')
      } finally {
        this.panelLoading = false
      }
    },

    async downloadKycDocument (doc) {
      const kycId = this.activePayload?.kyc?.kyc_id
      if (!kycId || !doc.sha256) return

      this.downloadingSha = doc.sha256
      try {
        const res = await this.executionStore.getKycDocument(kycId, doc.sha256)
        triggerBrowserDownload(res.data, doc.filename)
      } catch (error) {
        console.error('[DocumentsManagementView] downloadKycDocument:', error)
        useSnackbarStore().open(this.$t('documents.downloadFailed'), 'error')
      } finally {
        this.downloadingSha = null
      }
    },

    async downloadOperationDocument ({ operation, document: doc }) {
      this.downloadingOperation = {
        operationId: operation.operation_id,
        doctype: doc.doctype,
      }
      try {
        const res = await this.executionStore.getOperation(operation.operation_id, doc.doctype)
        triggerBrowserDownload(res.data, doc.filename)
      } catch (error) {
        console.error('[DocumentsManagementView] downloadOperationDocument:', error)
        useSnackbarStore().open(this.$t('documents.downloadFailed'), 'error')
      } finally {
        this.downloadingOperation = null
      }
    },

    async downloadCompanyZip (fileType) {
      const company = this.activePayload?.company
      if (!company) return

      const isKyc = fileType === 'kyc'
      if (isKyc) this.zippingKyc = true
      else this.zippingOperations = true

      try {
        const name = company.name || company.company_id
        await useImportFiles().downloadFiles(fileType, company.company_id, name, false)
      } catch (error) {
        console.error('[DocumentsManagementView] downloadCompanyZip:', error)
        useSnackbarStore().open(this.$t('documents.downloadFailed'), 'error')
      } finally {
        this.zippingKyc = false
        this.zippingOperations = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.documents-management-page {
  min-height: 100%;
}

.page-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding-bottom: 1rem;
}

.page-title {
  letter-spacing: -0.01em;
  color: #ffffff;
}
</style>
